import { Component, computed, inject, input, OnInit, Signal, signal } from '@angular/core';
import * as Models from '../models';
import { MatIconModule } from '@angular/material/icon';
import { MatAnchor, MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import * as _ from 'lodash-es';
import { ImageFallbackDirective } from '../img-fallback.directive';
import { ReportDomainService } from '../domain-services/report-domain.service';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { PackageDomainService } from '../domain-services/package-domain.service';
import { forkJoin, concatMap, finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-edit-reports',
  imports: [MatIconModule, MatButtonModule, RouterLink, ImageFallbackDirective],
  templateUrl: './edit-reports.html',
  styleUrl: './edit-reports.scss',
})
export class EditReports implements OnInit {
  clientReports = input.required<Models.Report[]>();
  clientPackages = input.required<Models.Package[]>();

  selectedItems = signal<Array<Models.Report | Models.Package>>([]);

  private readonly reportDomain = inject(ReportDomainService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly projectDomain = inject(ProjectDomainService);
  private readonly packageDomain = inject(PackageDomainService);
  private readonly snackBar = inject(MatSnackBar);
  isSubmitting = signal(false);
  apiError = signal<string | null>(null);

  readonly selectedReports = computed<Models.Report[]>(() => {
    const allSelected = this.selectedItems();
    const byId = new Map<string, Models.Report>();

    for (const item of allSelected) {
      if (this.isReport(item)) {
        byId.set(item.id, item);
      }
    }

    return Array.from(byId.values());
  });

  readonly selectedPackages = computed<Models.Package[]>(() => {
    return this.selectedItems().filter((i): i is Models.Package => this.isPackage(i));
  });

  constructor() {}

  ngOnInit(): void {
    const currentReports = this.isParticipantContext()
      ? (this.reportDomain.participantReports() ?? [])
      : (this.reportDomain.projectReports() ?? []);

    const currentPackages = this.isParticipantContext()
      ? (this.packageDomain.participantPackages() ?? [])
      : (this.packageDomain.projectPackages() ?? []);

    const sr = _.intersectionBy(this.clientReports(), currentReports, 'id');
    const sp = _.intersectionBy(this.clientPackages(), currentPackages, 'id');
    const all = _.union(sr, sp);
    this.selectedItems.set(all);
  }

  getItemImageUrl(item: Models.Report | Models.Package) {
    return (item as Models.Package).reports !== undefined ? 'Package' : item.id;
  }

  backToProjectDetail() {
    const resolvedProject =
      (this.route.snapshot.data['project'] as Models.Project | undefined) ?? null;
    const currentProject = this.projectDomain.currentProject() ?? resolvedProject;
    const projectId = currentProject?.id ?? this.getRouteParam('projectId');
    const participantId = this.getRouteParam('participantId');

    if (!projectId) {
      return;
    }

    if (participantId && this.isParticipantContext()) {
      this.router.navigate(['/projects', projectId, 'participants', participantId], {
        state: { showLoading: false },
      });
    } else {
      this.router.navigate(['/projects', projectId], { state: { showLoading: false } });
    }
  }

  selectItem(item: Models.Report | Models.Package) {
    if (this.isSubmitting()) {
      return;
    }

    if (this.isDisabled(item)) {
      const message =
        'This report is already included in a selected package. Remove the package to change this report.';
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    const currentProject = this.projectDomain.currentProject();
    const projectId = currentProject?.id ?? this.getRouteParam('projectId');
    const participantId = this.getRouteParam('participantId');

    if (!projectId) {
      const message = 'Missing project id.';
      this.apiError.set(message);
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    const inParticipantContext = this.isParticipantContext();
    if (inParticipantContext && !participantId) {
      const message = 'Missing participant id.';
      this.apiError.set(message);
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    const isPackageItem = this.isPackage(item);
    const isCurrentlySelected = this.isSelected(item);

    let op$;

    if (isPackageItem) {
      const pkgItem = item as Models.Package;
      const currentReports = inParticipantContext
        ? (this.reportDomain.participantReports() ?? [])
        : (this.reportDomain.projectReports() ?? []);

      if (isCurrentlySelected) {
        // Unassign the package
        op$ = inParticipantContext
          ? this.packageDomain.deleteParticipantPackage(participantId!, pkgItem.id)
          : this.packageDomain.deleteProjectPackage(projectId, pkgItem.id);
      } else {
        const conflictingReports = currentReports.filter((r) => pkgItem.reports.includes(r.id));
        if (conflictingReports.length > 0) {
          const removeConflicting$ = forkJoin(
            conflictingReports.map((r) =>
              inParticipantContext
                ? this.reportDomain.deleteParticipantReport(participantId!, r.id)
                : this.reportDomain.deleteProjectReport(projectId, r.id),
            ),
          );
          op$ = removeConflicting$.pipe(
            concatMap(() =>
              inParticipantContext
                ? this.packageDomain.addParticipantPackage(participantId!, pkgItem)
                : this.packageDomain.addProjectPackage(projectId, pkgItem),
            ),
          );
        } else {
          op$ = inParticipantContext
            ? this.packageDomain.addParticipantPackage(participantId!, pkgItem)
            : this.packageDomain.addProjectPackage(projectId, pkgItem);
        }
      }
    } else {
      const reportItem = item as Models.Report;
      const currentReports = inParticipantContext
        ? (this.reportDomain.participantReports() ?? [])
        : (this.reportDomain.projectReports() ?? []);
      const isAssigned = currentReports.some((r) => r.id === reportItem.id);

      if (isAssigned) {
        op$ = inParticipantContext
          ? this.reportDomain.deleteParticipantReport(participantId!, reportItem.id)
          : this.reportDomain.deleteProjectReport(projectId, reportItem.id);
      } else {
        op$ = inParticipantContext
          ? this.reportDomain.addParticipantReport(participantId!, reportItem)
          : this.reportDomain.addProjectReport(projectId, reportItem);
      }
    }

    this.isSubmitting.set(true);
    this.apiError.set(null);

    op$
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: () => {
          const current = this.selectedItems();

          if (isPackageItem) {
            const pkgItem = item as Models.Package;
            if (isCurrentlySelected) {
              // Remove the package from selected items
              this.selectedItems.set(
                current.filter((i) => !(this.isPackage(i) && i.id === pkgItem.id)),
              );
            } else {
              // Remove any conflicting reports from selected items and add the package
              const cleaned = current.filter(
                (i) => !(this.isReport(i) && pkgItem.reports.includes((i as Models.Report).id)),
              );
              this.selectedItems.set([...cleaned, pkgItem]);
            }
          } else {
            const reportItem = item as Models.Report;
            if (isCurrentlySelected) {
              this.selectedItems.set(
                current.filter((i) => !(this.isReport(i) && i.id === reportItem.id)),
              );
            } else {
              this.selectedItems.set([...current, reportItem]);
            }
          }
        },
        error: (err) => {
          const message = err?.error?.message || err?.message || 'Failed to update reports.';
          this.apiError.set(message);
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  isSelected(item: Models.Report | Models.Package) {
    return _.includes(this.selectedItems(), item);
  }

  private isPackage(item: Models.Report | Models.Package): item is Models.Package {
    return (item as Models.Package).reports !== undefined;
  }

  private isReport(item: Models.Report | Models.Package): item is Models.Report {
    return (item as Models.Package).reports === undefined;
  }

  isDisabled(item: Models.Report | Models.Package): boolean {
    if (this.isSelected(item)) {
      return false;
    }

    // Only reports can be disabled: when contained in an assigned package.
    if (!this.isReport(item)) {
      return false;
    }

    const selectedPackages = this.selectedPackages();
    return selectedPackages.some((pkg) => pkg.reports.includes(item.id));
  }

  private getRouteParam(name: string): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const value = route.snapshot.paramMap.get(name);
      if (value) {
        return value;
      }
      route = route.parent;
    }
    return null;
  }

  isParticipantContext(): boolean {
    if (this.route.snapshot.paramMap.get('participantId')) {
      return true;
    }

    let parent = this.route.parent;
    while (parent) {
      if (parent.snapshot.paramMap.get('participantId')) {
        return true;
      }
      parent = parent.parent;
    }

    return false;
  }
}
